import type { MenuProps } from 'antd';
import { Tag } from 'antd';
import {
    AccountBookOutlined,
    AppstoreOutlined,
    AuditOutlined,
    BankOutlined,
    BarcodeOutlined,
    BgColorsOutlined,
    CarOutlined,
    ContactsOutlined,
    CrownOutlined,
    DashboardOutlined,
    DatabaseOutlined,
    DollarOutlined,
    GiftOutlined,
    GlobalOutlined,
    GoldOutlined,
    InboxOutlined,
    KeyOutlined,
    LineChartOutlined,
    LockOutlined,
    MoneyCollectOutlined,
    NotificationOutlined,
    PrinterOutlined,
    QuestionCircleOutlined,
    SafetyCertificateOutlined,
    SettingOutlined,
    ShopOutlined,
    ShoppingCartOutlined,
    SyncOutlined,
    TagsOutlined,
    TeamOutlined,
    ToolOutlined,
    TrophyOutlined,
    UserOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import { ROUTES } from '@/constants/routes';
import type { PlanTier } from '@/redux/slices/planSlice';
import { PLAN_RANK } from '@/redux/slices/planSlice';

/**
 * Tier badge: [L] [S] [P] [E] — khớp SITEMAP v3.0 Legend.
 */
const TIER_COLORS = { L: 'default', S: 'blue', P: 'gold', E: 'red' } as const;

function tierBadge(tier: 'L' | 'S' | 'P' | 'E') {
    return (
        <Tag
            color={TIER_COLORS[tier]}
            style={{ fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '16px' }}
        >
            {tier}
        </Tag>
    );
}

function labelWithTier(label: string, tier?: 'L' | 'S' | 'P' | 'E') {
    if (!tier) return label;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>{label}</span>
            {tierBadge(tier)}
        </span>
    );
}

const ACCOUNTING_SECTION_ACCENTS: Record<string, string> = {
    'accounting-section-overview': '#60a5fa',
    'accounting-section-sales': '#22c55e',
    'accounting-section-purchase': '#f59e0b',
    'accounting-section-invoices': '#a78bfa',
    'accounting-section-cash-bank': '#06b6d4',
    'accounting-section-inventory': '#10b981',
    'accounting-section-assets': '#f97316',
    'accounting-section-debt': '#f43f5e',
    'accounting-section-reports': '#38bdf8',
    'accounting-section-settings': '#94a3b8',
};

function accountingSectionLabel(key: string, label: string) {
    return (
        <span className="erp-accounting-section-label">
            <span
                className="erp-accounting-section-dot"
                style={{ backgroundColor: ACCOUNTING_SECTION_ACCENTS[key] || '#94a3b8' }}
            />
            <span>{label}</span>
        </span>
    );
}

export type SidebarMenuItems = NonNullable<MenuProps['items']>;

interface BuildMenuOpts {
    hasPermission: (key: string) => boolean;
    onNavigate: (path: string) => void;
    userRole?: 'owner' | 'manager' | 'cashier' | 'inventory' | 'viewer';
    planTier?: PlanTier;
}

function meetsTierRequirement(currentTier: PlanTier, requiredTier: PlanTier): boolean {
    return PLAN_RANK[currentTier] >= PLAN_RANK[requiredTier];
}

/** Threshold số children → popup multi-column (2 cột) trên hover desktop. */
const MULTICOL_THRESHOLD = 8;

/**
 * Sidebar menu structure theo SITEMAP v3.0 (15 groups). Gom gọn từ ~25 nhóm
 * legacy → 12-15 nhóm chính. Group có nhiều children → popup multi-column
 * (CSS class `erp-menu-popup-wide` ở Sidebar/index.tsx) để giảm chiều dài.
 *
 * Lookup SITEMAP: `ERP-docs/tenant-app/SITEMAP.md` §1-15.
 */
export function buildSidebarMenuItems({
    hasPermission,
    onNavigate,
    userRole,
    planTier = 'lite',
}: BuildMenuOpts): SidebarMenuItems {
    const items: SidebarMenuItems = [];
    const isOwner = userRole === 'owner';
    const isManagerPlus = userRole === 'owner' || userRole === 'manager';
    const hasStarter = meetsTierRequirement(planTier, 'starter');
    const hasProfessional = meetsTierRequirement(planTier, 'professional');

    // Nhãn group — popupClassName chỉ gán khi children > MULTICOL_THRESHOLD.
    const widePopup = (label: string, tier?: 'L' | 'S' | 'P' | 'E') => ({
        label: labelWithTier(label, tier),
    });

    /** Gán popup multi-column class cho group có nhiều children (> threshold). */
    const applyPopupLayout = (group: SidebarMenuItems[number], children: SidebarMenuItems) => {
        if (children.length > MULTICOL_THRESHOLD) {
            (group as { popupClassName?: string }).popupClassName = 'erp-menu-popup-wide';
        }
        return group;
    };

    // ─── §1 Dashboard ─────────────────────────────────────────────────────────
    items.push({
        key: ROUTES.dashboard,
        icon: <DashboardOutlined />,
        label: 'Tổng quan',
        onClick: () => onNavigate(ROUTES.dashboard),
    });

    // ─── §2 Bán hàng (POS + tín dụng + vàng miếng) ─────────────────────────────
    // Gom: POS, Orders, Shifts, Pawn, Installment, Consignment,
    // GoldTrading. Children > 8 → multi-col popup.
    const salesChildren: SidebarMenuItems = [];
    if (hasPermission('pos.use')) {
        salesChildren.push({
            key: ROUTES.pos,
            icon: <ShoppingCartOutlined />,
            label: 'Bán hàng (POS)',
            onClick: () => onNavigate(ROUTES.pos),
        });
    }
    if (hasPermission('orders.view')) {
        salesChildren.push({
            key: ROUTES.orders.list,
            label: 'Đơn hàng',
            onClick: () => onNavigate(ROUTES.orders.list),
        });
    }
    if (hasPermission('pos.shifts_manage')) {
        salesChildren.push({
            key: ROUTES.posShifts.list,
            label: 'Ca làm việc',
            onClick: () => onNavigate(ROUTES.posShifts.list),
        });
    }
    salesChildren.push({ type: 'divider' as const });
    salesChildren.push({
        key: ROUTES.pawn.list,
        icon: <WalletOutlined />,
        label: labelWithTier('Cầm đồ', 'S'),
        onClick: () => onNavigate(ROUTES.pawn.list),
    });
    salesChildren.push({
        key: ROUTES.installment.list,
        icon: <BankOutlined />,
        label: labelWithTier('Bán trả góp', 'P'),
        onClick: () => onNavigate(ROUTES.installment.list),
    });

    if (salesChildren.length > 0) {
        items.push(applyPopupLayout({
            key: 'sales-group',
            icon: <ShoppingCartOutlined />,
            ...widePopup('Bán hàng'),
            children: salesChildren,
        }, salesChildren));
    }

    // ─── §3 Mua vàng cũ ─────────────────────────────

    const buyBackChildren: SidebarMenuItems = []

    buyBackChildren.push({
        key: ROUTES.buyback.list,
        icon: <DollarOutlined />,
        label: labelWithTier('Đơn mua vàng cũ', 'S'),
        onClick: () => onNavigate(ROUTES.buyback.list),
    })

    buyBackChildren.push({
        key: ROUTES.buyback.new,
        icon: <DollarOutlined />,
        label: labelWithTier('Mua vàng cũ', 'S'),
        onClick: () => onNavigate(ROUTES.buyback.new),
    });



    if (buyBackChildren.length > 0) {
        items.push(applyPopupLayout({
            key: 'buy-back-group',
            icon: <ShoppingCartOutlined />,
            ...widePopup('Mua vàng cũ'),
            children: buyBackChildren,
        }, buyBackChildren));
    }

    if (hasPermission('pos.refund')) {
        const exchangeChildren: SidebarMenuItems = [
            {
                key: ROUTES.exchanges.list,
                icon: <SyncOutlined />,
                label: 'Đơn thu đổi',
                onClick: () => onNavigate(ROUTES.exchanges.list),
            },
            {
                key: ROUTES.exchanges.new,
                icon: <SyncOutlined />,
                label: 'Tạo đơn thu đổi',
                onClick: () => onNavigate(ROUTES.exchanges.new),
            },
        ];
        items.push(applyPopupLayout({
            key: 'exchange-group',
            icon: <SyncOutlined />,
            ...widePopup('Thu đổi', 'S'),
            children: exchangeChildren,
        }, exchangeChildren));
    }


    // ─── §5 Sản phẩm + Khuyến mãi + In tem ─────────────────────────────────────
    if (hasPermission('products.view')) {
        items.push({
            key: 'products-group',
            icon: <GoldOutlined />,
            label: 'Sản phẩm',
            children: [
                {
                    key: ROUTES.products.list,
                    label: 'Danh sách sản phẩm',
                    onClick: () => onNavigate(ROUTES.products.list),
                },
                {
                    key: ROUTES.products.categories.list,
                    label: 'Danh mục',
                    onClick: () => onNavigate(ROUTES.products.categories.list),
                },
                {
                    key: ROUTES.products.metalCategories.list,
                    label: 'Danh mục kim loại',
                    onClick: () => onNavigate(ROUTES.products.metalCategories.list),
                },
                {
                    key: ROUTES.products.brands.list,
                    label: labelWithTier('Thương hiệu', 'S'),
                    onClick: () => onNavigate(ROUTES.products.brands.list),
                },
                {
                    key: ROUTES.products.attributes.list,
                    label: labelWithTier('Thuộc tính', 'S'),
                    onClick: () => onNavigate(ROUTES.products.attributes.list),
                },
                {
                    key: ROUTES.products.units,
                    label: 'Đơn vị tính',
                    onClick: () => onNavigate(ROUTES.products.units),
                },
                { type: 'divider' as const },
                {
                    key: ROUTES.promotions.list,
                    icon: <TagsOutlined />,
                    label: labelWithTier('Khuyến mãi', 'S'),
                    onClick: () => onNavigate(ROUTES.promotions.list),
                },
                {
                    key: ROUTES.promotions.vouchers,
                    label: labelWithTier('Voucher', 'S'),
                    onClick: () => onNavigate(ROUTES.promotions.vouchers),
                },
                {
                    key: ROUTES.promotions.flashSales,
                    label: labelWithTier('Flash sale', 'P'),
                    onClick: () => onNavigate(ROUTES.promotions.flashSales),
                },
                {
                    key: ROUTES.promotions.loyalty,
                    icon: <TrophyOutlined />,
                    label: labelWithTier('Tích điểm', 'S'),
                    onClick: () => onNavigate(ROUTES.promotions.loyalty),
                },
                {
                    key: ROUTES.promotions.wholesaleGifts,
                    icon: <GiftOutlined />,
                    label: labelWithTier('Quà tặng sỉ', 'P'),
                    onClick: () => onNavigate(ROUTES.promotions.wholesaleGifts),
                },
                {
                    key: ROUTES.promotions.reports,
                    icon: <LineChartOutlined />,
                    label: labelWithTier('Báo cáo KM', 'S'),
                    onClick: () => onNavigate(ROUTES.promotions.reports),
                },
                { type: 'divider' as const },
                {
                    key: ROUTES.labels.list,
                    icon: <BarcodeOutlined />,
                    label: labelWithTier('In tem / Mã vạch', 'S'),
                    onClick: () => onNavigate(ROUTES.labels.list),
                },
            ],
            popupClassName: 'erp-menu-popup-wide',
        });
    }

    // ─── §4 Kho hàng ──────────────────────────────────────────────────────────
    if (hasPermission('inventory.view')) {
        const invChildren: SidebarMenuItems = [
            {
                key: ROUTES.inventory.list,
                label: 'Tồn kho',
                onClick: () => onNavigate(ROUTES.inventory.list),
            },
            {
                key: ROUTES.inventory.warehouses,
                label: 'Quản lý kho',
                onClick: () => onNavigate(ROUTES.inventory.warehouses),
            },
            {
                key: ROUTES.inventory.alerts,
                label: 'Cảnh báo tồn',
                onClick: () => onNavigate(ROUTES.inventory.alerts),
            },
            {
                key: ROUTES.inventory.movements,
                label: 'Lịch sử biến động',
                onClick: () => onNavigate(ROUTES.inventory.movements),
            },
        ];
        if (hasPermission('inventory.stock_in')) {
            invChildren.push({ type: 'divider' as const });
            invChildren.push({
                key: ROUTES.inventory.receipts,
                label: 'Phiếu nhập',
                onClick: () => onNavigate(ROUTES.inventory.receipts),
            });
            invChildren.push({
                key: ROUTES.inventory.issues,
                label: 'Phiếu xuất',
                onClick: () => onNavigate(ROUTES.inventory.issues),
            });
        }
        if (hasPermission('inventory.transfer')) {
            invChildren.push({
                key: ROUTES.inventory.transfers,
                label: labelWithTier('Chuyển kho', 'P'),
                onClick: () => onNavigate(ROUTES.inventory.transfers),
            });
        }
        if (hasPermission('inventory.adjust')) {
            invChildren.push({
                key: ROUTES.inventory.counts,
                label: 'Kiểm kê',
                onClick: () => onNavigate(ROUTES.inventory.counts),
            });
        }
        items.push({
            key: 'inventory-group',
            icon: <InboxOutlined />,
            label: 'Kho hàng',
            children: invChildren,
            popupClassName:
                invChildren.length >= MULTICOL_THRESHOLD ? 'erp-menu-popup-wide' : undefined,
        });
    }

    // ─── §6 Giao vận ──────────────────────────────────────────────────────────
    if (hasPermission('shipping.view')) {
        items.push({
            key: 'shipping-group',
            icon: <CarOutlined />,
            label: labelWithTier('Giao vận', 'P'),
            children: [
                {
                    key: ROUTES.shipping.list,
                    label: 'Đơn giao vận',
                    onClick: () => onNavigate(ROUTES.shipping.list),
                },
                {
                    key: ROUTES.shipping.pickupLocations,
                    label: 'Nơi lấy hàng',
                    onClick: () => onNavigate(ROUTES.shipping.pickupLocations),
                },
                {
                    key: ROUTES.shipping.ghtkLogs,
                    label: 'Logs GHTK',
                    onClick: () => onNavigate(ROUTES.shipping.ghtkLogs),
                },
            ],
        });
    }

    // ─── §7 Xưởng sửa chữa ────────────────────────────────────────────────────
    if (hasPermission('workshop.view') || hasStarter) {
        items.push({
            key: 'workshop-group',
            icon: <ToolOutlined />,
            label: labelWithTier('Xưởng', 'S'),
            children: [
                {
                    key: ROUTES.workshop.new,
                    label: 'Tạo đơn mới',
                    onClick: () => onNavigate(ROUTES.workshop.new),
                },
                {
                    key: ROUTES.workshop.list,
                    label: 'Đơn sửa chữa',
                    onClick: () => onNavigate(ROUTES.workshop.list),
                },
                {
                    key: ROUTES.workshop.craftsmen,
                    label: 'Thợ',
                    onClick: () => onNavigate(ROUTES.workshop.craftsmen),
                },
                {
                    key: ROUTES.workshop.services,
                    label: 'Dịch vụ',
                    onClick: () => onNavigate(ROUTES.workshop.services),
                },
                {
                    key: ROUTES.jewelryScale.index,
                    label: 'Màn hình cân',
                    onClick: () => onNavigate(ROUTES.jewelryScale.index),
                }
            ],
        });
    }

    // ─── §8 Giá vàng ──────────────────────────────────────────────────────────
    items.push({
        key: 'gold-price-group',
        icon: <LineChartOutlined />,
        label: labelWithTier('Giá vàng', 'S'),
        children: [
            {
                key: ROUTES.goldPrice.board,
                label: 'Bảng giá',
                onClick: () => onNavigate(ROUTES.goldPrice.board),
            },
            {
                key: ROUTES.goldPrice.history,
                label: 'Lịch sử giá',
                onClick: () => onNavigate(ROUTES.goldPrice.history),
            },
            // TODO(spec §12.A #8): wire current_quotes:view permission gate when matcher
            // supports colon-notation (currently no project-wide matcher found).
            {
                key: ROUTES.goldPrice.currentQuotes,
                label: 'Tỉ giá vàng bạc',
                onClick: () => onNavigate(ROUTES.goldPrice.currentQuotes),
            },
            // ─── MỚI Plan 3 — Danh mục vàng bạc ───
            // TODO(spec §12.A #8): wire instruments:view permission gate when matcher supports colon
            {
                key: ROUTES.goldPrice.instruments,
                label: 'Danh mục vàng bạc',
                onClick: () => onNavigate(ROUTES.goldPrice.instruments),
            },
            {
                key: ROUTES.goldPrice.tv,
                label: 'TV Display',
                onClick: () => onNavigate(ROUTES.goldPrice.tv),
            },
            {
                key: ROUTES.goldPrice.widget,
                label: 'Widget nhúng',
                onClick: () => onNavigate(ROUTES.goldPrice.widget),
            },
            {
                key: ROUTES.goldPrice.settings,
                label: 'Cấu hình',
                onClick: () => onNavigate(ROUTES.goldPrice.settings),
            },
            {
                key: ROUTES.goldPrice.priceProtection,
                label: 'Bảo vệ giá',
                onClick: () => onNavigate(ROUTES.goldPrice.priceProtection),
            },
        ],
    });

    // ─── §9 Mua hàng (Purchasing) ─────────────────────────────────────────────
    if (hasPermission('purchasing.view')) {
        items.push({
            key: 'purchasing-group',
            icon: <ShoppingCartOutlined />,
            label: labelWithTier('Mua hàng', 'S'),
            children: [
                {
                    key: ROUTES.purchasing.list,
                    label: 'Đơn đặt hàng',
                    onClick: () => onNavigate(ROUTES.purchasing.list),
                },
                {
                    key: ROUTES.purchasing.new,
                    label: 'Tạo PO mới',
                    onClick: () => onNavigate(ROUTES.purchasing.new),
                },
            ],
        });
    }

    // ─── §10 Khách hàng & Đối tác ────────────────────────────────────────────
    // Gom: Khách hàng, CRM, NCC, Đặt hàng, Hỗ trợ.
    if (hasPermission('customers.view') || hasPermission('suppliers.view')) {
        const partnersChildren: SidebarMenuItems = [];
        if (hasPermission('customers.view')) {
            partnersChildren.push({
                key: ROUTES.customers.list,
                icon: <ContactsOutlined />,
                label: 'Khách hàng',
                onClick: () => onNavigate(ROUTES.customers.list),
            });
            partnersChildren.push({
                key: ROUTES.crm.followUps,
                label: labelWithTier('CRM Follow-up', 'P'),
                onClick: () => onNavigate(ROUTES.crm.followUps),
            });
            partnersChildren.push({
                key: ROUTES.crm.dashboard,
                label: labelWithTier('CRM Dashboard', 'P'),
                onClick: () => onNavigate(ROUTES.crm.dashboard),
            });
            // EG-375 — Referral program (Starter+)
            partnersChildren.push({
                key: ROUTES.referral.overview,
                label: labelWithTier('Chương trình giới thiệu', 'S'),
                onClick: () => onNavigate(ROUTES.referral.overview),
            });
        }
        if (hasPermission('suppliers.view')) {
            partnersChildren.push({ type: 'divider' as const });
            partnersChildren.push({
                key: ROUTES.suppliers.list,
                label: 'Nhà cung cấp',
                onClick: () => onNavigate(ROUTES.suppliers.list),
            });
        }
        partnersChildren.push({ type: 'divider' as const });
        partnersChildren.push({
            key: ROUTES.support.tickets,
            icon: <QuestionCircleOutlined />,
            label: 'Ticket hỗ trợ',
            onClick: () => onNavigate(ROUTES.support.tickets),
        });

        items.push({
            key: 'partners-group',
            icon: <TeamOutlined />,
            label: 'Khách hàng & Đối tác',
            children: partnersChildren,
            popupClassName:
                partnersChildren.length >= MULTICOL_THRESHOLD ? 'erp-menu-popup-wide' : undefined,
        });
    }

    // ─── §12 Hoa hồng & KPI ───────────────────────────────────────────────────
    // Owner có rất nhiều config items → multi-col popup.
    const commissionChildren: SidebarMenuItems = [
        {
            key: ROUTES.commission.index,
            label: 'Tổng quan',
            onClick: () => onNavigate(ROUTES.commission.index),
        },
        {
            key: ROUTES.commission.my,
            label: 'HH của tôi',
            onClick: () => onNavigate(ROUTES.commission.my),
        },
        {
            key: ROUTES.commission.kpi,
            label: 'KPI Dashboard',
            onClick: () => onNavigate(ROUTES.commission.kpi),
        },
        {
            key: ROUTES.commission.leaderboard,
            label: 'Bảng xếp hạng',
            onClick: () => onNavigate(ROUTES.commission.leaderboard),
        },
    ];
    if (isManagerPlus) {
        commissionChildren.push(
            {
                key: ROUTES.commission.kpiTargets,
                label: 'Đặt KPI Target',
                onClick: () => onNavigate(ROUTES.commission.kpiTargets),
            },
            {
                key: ROUTES.commission.liveSessions,
                label: 'Phiên livestream',
                onClick: () => onNavigate(ROUTES.commission.liveSessions),
            },
            {
                key: ROUTES.commission.reports,
                label: 'Báo cáo HH',
                onClick: () => onNavigate(ROUTES.commission.reports),
            },
        );
    }
    if (isOwner) {
        commissionChildren.push({ type: 'divider' as const });
        commissionChildren.push(
            {
                key: ROUTES.commission.roles,
                label: '⚙️ 1. Vai trò HH',
                onClick: () => onNavigate(ROUTES.commission.roles),
            },
            {
                key: ROUTES.commission.configGeneral,
                label: '⚙️ 2. Cơ chế tính',
                onClick: () => onNavigate(ROUTES.commission.configGeneral),
            },
            {
                key: ROUTES.commission.productRates,
                label: '⚙️ 3. HH theo SP',
                onClick: () => onNavigate(ROUTES.commission.productRates),
            },
            {
                key: ROUTES.commission.bonusThresholds,
                label: '⚙️ 4. Mốc thưởng',
                onClick: () => onNavigate(ROUTES.commission.bonusThresholds),
            },
            { type: 'divider' as const },
            {
                key: ROUTES.commission.payouts,
                label: 'Duyệt + chi trả',
                onClick: () => onNavigate(ROUTES.commission.payouts),
            },
            {
                key: ROUTES.commission.review,
                label: 'Review per-staff',
                onClick: () => onNavigate(ROUTES.commission.review),
            },
            {
                key: ROUTES.commission.adjustments,
                label: 'Điều chỉnh + Audit',
                onClick: () => onNavigate(ROUTES.commission.adjustments),
            },
        );
    }
    items.push({
        key: 'commission-group',
        icon: <TrophyOutlined />,
        label: labelWithTier('Hoa hồng & KPI', 'S'),
        children: commissionChildren,
        popupClassName:
            commissionChildren.length >= MULTICOL_THRESHOLD ? 'erp-menu-popup-wide' : undefined,
    });

    // ─── §13 Báo cáo ───────────────────────────────────────────────────────
    const canSeeCoreReportsMenu =
        hasPermission('reports.revenue') ||
        hasPermission('reports.inventory') ||
        hasPermission('finance.view');
    const canSeeFeatureReportsMenu = isManagerPlus && hasStarter;

    if (canSeeCoreReportsMenu || canSeeFeatureReportsMenu) {
        const reportsChildren: SidebarMenuItems = [];

        if (canSeeCoreReportsMenu) {
            reportsChildren.push({
                key: ROUTES.reports.index,
                label: 'Tổng quan báo cáo',
                onClick: () => onNavigate(ROUTES.reports.index),
            });
        }
        if (hasPermission('reports.revenue')) {
            reportsChildren.push({
                key: ROUTES.reports.revenue,
                label: 'Doanh thu',
                onClick: () => onNavigate(ROUTES.reports.revenue),
            });
        }
        if (hasPermission('reports.inventory')) {
            reportsChildren.push({
                key: ROUTES.reports.inventory,
                label: 'Tồn kho',
                onClick: () => onNavigate(ROUTES.reports.inventory),
            });
        }

        if (canSeeFeatureReportsMenu) {
            if (reportsChildren.length > 0) reportsChildren.push({ type: 'divider' as const });
            reportsChildren.push(
                {
                    key: ROUTES.reports.pawn,
                    label: labelWithTier('Báo cáo cầm đồ', 'S'),
                    onClick: () => onNavigate(ROUTES.reports.pawn),
                },
                {
                    key: ROUTES.reports.workshop,
                    label: labelWithTier('Báo cáo xưởng', 'S'),
                    onClick: () => onNavigate(ROUTES.reports.workshop),
                },
                {
                    key: ROUTES.reports.exchange,
                    label: labelWithTier('Báo cáo thu đổi', 'S'),
                    onClick: () => onNavigate(ROUTES.reports.exchange),
                },
                {
                    key: ROUTES.reports.buyback,
                    label: labelWithTier('Báo cáo mua vàng cũ', 'S'),
                    onClick: () => onNavigate(ROUTES.reports.buyback),
                },
            );
            if (hasProfessional) {
                reportsChildren.push({
                    key: ROUTES.reports.installment,
                    label: labelWithTier('Báo cáo trả góp', 'P'),
                    onClick: () => onNavigate(ROUTES.reports.installment),
                });
            }
        }

        if (canSeeCoreReportsMenu) {
            reportsChildren.push({ type: 'divider' as const });
            reportsChildren.push(
                {
                    key: ROUTES.reportsAdvanced.staff,
                    label: labelWithTier('KPI nhân viên', 'P'),
                    onClick: () => onNavigate(ROUTES.reportsAdvanced.staff),
                },
                {
                    key: ROUTES.reportsAdvanced.customers,
                    label: labelWithTier('Khách hàng CLV/RFM', 'P'),
                    onClick: () => onNavigate(ROUTES.reportsAdvanced.customers),
                },
                {
                    key: ROUTES.reportsAdvanced.products,
                    label: 'Top sản phẩm',
                    onClick: () => onNavigate(ROUTES.reportsAdvanced.products),
                },
                {
                    key: ROUTES.reportsAdvanced.multiStore,
                    label: labelWithTier('So sánh chi nhánh', 'P'),
                    onClick: () => onNavigate(ROUTES.reportsAdvanced.multiStore),
                },
                {
                    key: ROUTES.reportsAdvanced.builder,
                    label: labelWithTier('Report Builder', 'E'),
                    onClick: () => onNavigate(ROUTES.reportsAdvanced.builder),
                },
            );
        }

        items.push({
            key: 'reports-group',
            icon: <LineChartOutlined />,
            label: 'Báo cáo',
            children: reportsChildren,
            popupClassName: 'erp-menu-popup-wide',
        });
    }

    // ─── §13b Tài chính ──────────────────────────────────────────────────
    if (
        hasPermission('reports.revenue') ||
        hasPermission('reports.inventory') ||
        hasPermission('finance.view')
    ) {
        const financeChildren: SidebarMenuItems = [
            {
                key: ROUTES.reportsAdvanced.profit,
                label: labelWithTier('Lợi nhuận (COGS)', 'P'),
                onClick: () => onNavigate(ROUTES.reportsAdvanced.profit),
            },
            {
                key: ROUTES.reports.eod,
                icon: <MoneyCollectOutlined />,
                label: 'Cuối ngày (EOD)',
                onClick: () => onNavigate(ROUTES.reports.eod),
            },
            {
                key: ROUTES.finance.cashBook,
                label: 'Sổ quỹ tiền mặt',
                onClick: () => onNavigate(ROUTES.finance.cashBook),
            },
            {
                key: ROUTES.finance.vouchers,
                label: 'Phiếu thu / chi',
                onClick: () => onNavigate(ROUTES.finance.vouchers),
            },
            {
                key: ROUTES.finance.einvoiceList,
                label: labelWithTier('Hoá đơn điện tử', 'S'),
                onClick: () => onNavigate(ROUTES.finance.einvoiceList),
            },
            {
                key: ROUTES.finance.misa,
                label: labelWithTier('MISA AMIS', 'E'),
                onClick: () => onNavigate(ROUTES.finance.misa),
            },
        ];

        items.push({
            key: 'finance-group',
            icon: <BankOutlined />,
            label: 'Tài chính',
            children: financeChildren,
            popupClassName: 'erp-menu-popup-wide',
        });
    }

    // ─── §14 Kế toán — INDEPENDENT block (NOT inside reports if) ─────────────
    // Full menu theo feature-screens.md. Sections are nested to keep the accounting popup compact.
    if (
        hasPermission('accounting.reports') ||
        hasPermission('accounting.vouchers') ||
        hasPermission('accounting.settings') ||
        hasPermission('accounting.einvoice') ||
        hasPermission('accounting.inventory') ||
        hasPermission('accounting.ar')
    ) {
        const acctChildren: SidebarMenuItems = [];
        const pushAcctSection = (key: string, label: string, children: SidebarMenuItems) => {
            if (children.length === 0) return;
            acctChildren.push({
                key,
                label: accountingSectionLabel(key, label),
                children,
                popupClassName: 'erp-menu-popup-accounting-section',
            });
        };

        pushAcctSection('accounting-section-overview', 'Tổng quan', [
            {
                key: ROUTES.accounting.dashboard,
                label: 'Tổng quan kế toán',
                onClick: () => onNavigate(ROUTES.accounting.dashboard),
            },
        ]);

        // Bán hàng kế toán
        if (hasPermission('accounting.vouchers')) {
            pushAcctSection('accounting-section-sales', 'Bán hàng', [
                { key: ROUTES.accounting.salesDocuments, label: 'Chứng từ bán hàng', onClick: () => onNavigate(ROUTES.accounting.salesDocuments) },
            ]);
        }

        // Mua hàng kế toán
        const purchaseAccountingChildren: SidebarMenuItems = [];
        if (hasPermission('purchasing.view')) {
            purchaseAccountingChildren.push(
                { key: ROUTES.purchasing.list + '__acct', label: 'Đơn mua hàng', onClick: () => onNavigate(ROUTES.purchasing.list) },
                { key: ROUTES.purchasing.new + '__acct', label: 'Tạo PO mới', onClick: () => onNavigate(ROUTES.purchasing.new) },
            );
        }
        if (hasPermission('accounting.vouchers')) {
            purchaseAccountingChildren.push(
                { key: ROUTES.accounting.purchaseInvoices, label: 'Chứng từ mua hàng', onClick: () => onNavigate(ROUTES.accounting.purchaseInvoices) },
                { key: ROUTES.accounting.purchaseReturns, label: 'Trả hàng mua', onClick: () => onNavigate(ROUTES.accounting.purchaseReturns) },
                { key: ROUTES.accounting.purchaseDiscounts, label: 'Giảm giá mua', onClick: () => onNavigate(ROUTES.accounting.purchaseDiscounts) },
            );
        }
        pushAcctSection('accounting-section-purchase', 'Mua hàng', purchaseAccountingChildren);

        // Hóa đơn
        const invoiceChildren: SidebarMenuItems = [];
        if (hasPermission('accounting.vouchers')) {
            invoiceChildren.push({
                key: ROUTES.accounting.salesEinvoices,
                label: 'Hóa đơn đầu ra',
                onClick: () => onNavigate(ROUTES.accounting.salesEinvoices),
            });
        }
        if (hasPermission('accounting.einvoice')) {
            invoiceChildren.push({
                key: ROUTES.accounting.govInvoices,
                label: 'Hóa đơn đầu vào',
                onClick: () => onNavigate(ROUTES.accounting.govInvoices),
            });
        }
        pushAcctSection('accounting-section-invoices', 'Hóa đơn', invoiceChildren);

        // Tiền mặt, ngân hàng
        if (hasPermission('accounting.vouchers')) {
            pushAcctSection('accounting-section-cash-bank', 'Tiền mặt, ngân hàng', [
                { key: ROUTES.accounting.transactions, label: 'Sổ thu chi', onClick: () => onNavigate(ROUTES.accounting.transactions) },
                { key: ROUTES.accounting.receiptNew, label: 'Tạo phiếu thu', onClick: () => onNavigate(ROUTES.accounting.receiptNew) },
                { key: ROUTES.accounting.paymentNew, label: 'Tạo phiếu chi', onClick: () => onNavigate(ROUTES.accounting.paymentNew) },
                { key: ROUTES.accounting.internalTransferNew, label: 'Chuyển tiền nội bộ', onClick: () => onNavigate(ROUTES.accounting.internalTransferNew) },
            ]);
        }

        // Kho kế toán
        if (hasPermission('accounting.inventory')) {
            pushAcctSection('accounting-section-inventory', 'Kho kế toán', [
                { key: ROUTES.accounting.inventoryReceipts, label: 'Phiếu nhập kho', onClick: () => onNavigate(ROUTES.accounting.inventoryReceipts) },
                { key: ROUTES.accounting.inventoryIssues, label: 'Phiếu xuất kho', onClick: () => onNavigate(ROUTES.accounting.inventoryIssues) },
                { key: ROUTES.accounting.inventoryTransfers, label: 'Chuyển kho', onClick: () => onNavigate(ROUTES.accounting.inventoryTransfers) },
                { key: ROUTES.accounting.productsUom, label: 'Đơn vị tính', onClick: () => onNavigate(ROUTES.accounting.productsUom) },
            ]);
        }

        // Tài sản & phân bổ
        if (hasPermission('accounting.vouchers')) {
            pushAcctSection('accounting-section-assets', 'Tài sản & phân bổ', [
                { key: ROUTES.accounting.prepaidExpenses, label: 'Chi phí trả trước', onClick: () => onNavigate(ROUTES.accounting.prepaidExpenses) },
                { key: ROUTES.accounting.fixedAssets, label: 'Tài sản cố định', onClick: () => onNavigate(ROUTES.accounting.fixedAssets) },
                { key: ROUTES.accounting.toolSupplies, label: 'Công cụ dụng cụ', onClick: () => onNavigate(ROUTES.accounting.toolSupplies) },
                { key: ROUTES.accounting.toolSupplyReports, label: 'Báo cáo CCDC', onClick: () => onNavigate(ROUTES.accounting.toolSupplyReports) },
                { key: ROUTES.accounting.otherDocument, label: 'Chứng từ nghiệp vụ khác', onClick: () => onNavigate(ROUTES.accounting.otherDocument) },
            ]);
        }

        // Công nợ
        if (hasPermission('accounting.ar')) {
            pushAcctSection('accounting-section-debt', 'Công nợ', [
                {
                    key: ROUTES.accounting.customersAR,
                    label: 'Công nợ khách hàng',
                    onClick: () => onNavigate(ROUTES.accounting.customersAR),
                },
            ]);
        }

        // Báo cáo kế toán
        if (hasPermission('accounting.reports')) {
            pushAcctSection('accounting-section-reports', 'Báo cáo', [
                { key: ROUTES.accounting.reports + '__acct', label: 'Tổng quan báo cáo', onClick: () => onNavigate(ROUTES.accounting.reports) },
                { key: ROUTES.accounting.reportBusinessResults, label: 'Kết quả kinh doanh', onClick: () => onNavigate(ROUTES.accounting.reportBusinessResults) },
                { key: ROUTES.accounting.reportBalanceSheet, label: 'Bảng cân đối kế toán', onClick: () => onNavigate(ROUTES.accounting.reportBalanceSheet) },
                { key: ROUTES.accounting.reportTrialBalance, label: 'Bảng cân đối phát sinh', onClick: () => onNavigate(ROUTES.accounting.reportTrialBalance) },
                { key: ROUTES.accounting.reportCashBook, label: 'Sổ quỹ', onClick: () => onNavigate(ROUTES.accounting.reportCashBook) },
                { key: ROUTES.accounting.reportBankDepositBook, label: 'Sổ tiền gửi NH', onClick: () => onNavigate(ROUTES.accounting.reportBankDepositBook) },
                { key: ROUTES.accounting.reportCashReceiptJournal, label: 'NK thu tiền', onClick: () => onNavigate(ROUTES.accounting.reportCashReceiptJournal) },
                { key: ROUTES.accounting.reportCashPaymentJournal, label: 'NK chi tiền', onClick: () => onNavigate(ROUTES.accounting.reportCashPaymentJournal) },
                { key: ROUTES.accounting.reportCashFlowStatement, label: 'BC LCTT', onClick: () => onNavigate(ROUTES.accounting.reportCashFlowStatement) },
                { key: ROUTES.accounting.reportS2b, label: 'S2b — Doanh thu bán hàng', onClick: () => onNavigate(ROUTES.accounting.reportS2b) },
                { key: ROUTES.accounting.reportS2c, label: 'S2c — DT, chi phí', onClick: () => onNavigate(ROUTES.accounting.reportS2c) },
                { key: ROUTES.accounting.reportS2d, label: 'S2d — Chi tiết vật tư', onClick: () => onNavigate(ROUTES.accounting.reportS2d) },
                { key: ROUTES.accounting.reportS2e, label: 'S2e — Chi tiết tiền', onClick: () => onNavigate(ROUTES.accounting.reportS2e) },
                { key: ROUTES.accounting.reportInventorySummary, label: 'Tổng hợp nhập xuất tồn', onClick: () => onNavigate(ROUTES.accounting.reportInventorySummary) },
                { key: ROUTES.accounting.reportDocumentListing, label: 'Bảng kê chứng từ', onClick: () => onNavigate(ROUTES.accounting.reportDocumentListing) },
                { key: ROUTES.accounting.reportVoucherListing, label: 'Bảng kê phiếu thu chi', onClick: () => onNavigate(ROUTES.accounting.reportVoucherListing) },
                { key: ROUTES.accounting.reportInvoiceListing, label: 'Bảng kê hoá đơn', onClick: () => onNavigate(ROUTES.accounting.reportInvoiceListing) },
                { key: ROUTES.accounting.reportWarehouseVoucher, label: 'Bảng kê phiếu kho', onClick: () => onNavigate(ROUTES.accounting.reportWarehouseVoucher) },
                { key: ROUTES.accounting.reportReceivablePayable, label: 'Công nợ phải thu/trả', onClick: () => onNavigate(ROUTES.accounting.reportReceivablePayable) },
                { key: ROUTES.accounting.reportCustomersAR, label: 'Báo cáo công nợ KH', onClick: () => onNavigate(ROUTES.accounting.reportCustomersAR) },
            ]);
        }

        // Cài đặt kế toán (owner only)
        if (hasPermission('accounting.settings') && isOwner) {
            pushAcctSection('accounting-section-settings', 'Thiết lập', [
                {
                    key: ROUTES.accounting.dataRepair,
                    label: 'Repair dữ liệu kế toán',
                    onClick: () => onNavigate(ROUTES.accounting.dataRepair),
                },
                {
                    key: ROUTES.accounting.settings,
                    label: '⚙️ Cài đặt kế toán',
                    onClick: () => onNavigate(ROUTES.accounting.settings),
                },
            ]);
        }

        items.push({
            key: 'accounting-group',
            icon: <AccountBookOutlined />,
            label: labelWithTier('Kế toán', 'E'),
            children: acctChildren,
            popupClassName: 'erp-menu-popup-accounting',
        });
    }

    // ─── Lương (Payroll) — group riêng ────────────────────────────────────────
    if (hasPermission('payroll.view')) {
        items.push({
            key: 'payroll-group',
            icon: <WalletOutlined />,
            label: labelWithTier('Lương', 'S'),
            children: [
                {
                    key: ROUTES.payroll.dashboard,
                    label: 'Tổng quan lương',
                    onClick: () => onNavigate(ROUTES.payroll.dashboard),
                },
                {
                    key: ROUTES.payroll.runs,
                    label: 'Bảng lương',
                    onClick: () => onNavigate(ROUTES.payroll.runs),
                },
                {
                    key: ROUTES.payroll.components,
                    label: 'Thành phần lương',
                    onClick: () => onNavigate(ROUTES.payroll.components),
                },
                {
                    key: ROUTES.payroll.reports.s5hkd,
                    label: 'Báo cáo S5-HKD',
                    onClick: () => onNavigate(ROUTES.payroll.reports.s5hkd),
                },
                {
                    key: ROUTES.payroll.reports.qttTncn,
                    label: 'Quyết toán TNCN',
                    onClick: () => onNavigate(ROUTES.payroll.reports.qttTncn),
                },
                { type: 'divider' as const },
                {
                    key: ROUTES.me.payslips,
                    label: 'Phiếu lương của tôi',
                    onClick: () => onNavigate(ROUTES.me.payslips),
                },
            ],
            disabled: !hasStarter,
            style: !hasStarter ? { opacity: 0.5, cursor: 'not-allowed' } : undefined,
            title: !hasStarter ? 'Cần gói Starter trở lên' : undefined,
        });
    }

    // ─── §15 Cài đặt — group lớn nhất, multi-col popup ────────────────────────
    // Gom: Doanh nghiệp, Chi nhánh, Nhân viên, Phần cứng, Giao diện, Phân quyền,
    // Loyalty, Thông báo, Locale, Billing, Profile, Security, API Keys, Audit,
    // Import dữ liệu, Hỗ trợ.
    const settingsChildren: SidebarMenuItems = [];

    // — Doanh nghiệp —
    if (hasPermission('settings.business')) {
        settingsChildren.push({
            key: ROUTES.settings.business,
            icon: <ShopOutlined />,
            label: 'Doanh nghiệp',
            onClick: () => onNavigate(ROUTES.settings.business),
        });
    }
    if (hasPermission('settings.business')) {
        settingsChildren.push({
            key: `${ROUTES.stores.list}__shortcut`,
            icon: <ShopOutlined />,
            label: labelWithTier('Chuỗi cửa hàng', 'P'),
            onClick: () => onNavigate(ROUTES.stores.list),
        });
    }
    if (hasPermission('staff.view')) {
        settingsChildren.push({
            key: ROUTES.staff.list,
            icon: <TeamOutlined />,
            label: 'Nhân viên',
            onClick: () => onNavigate(ROUTES.staff.list),
        });
    }
    if (hasPermission('settings.payroll')) {
        settingsChildren.push({
            key: ROUTES.settings.payroll,
            icon: <WalletOutlined />,
            label: labelWithTier('Cài đặt lương', 'S'),
            onClick: () => onNavigate(ROUTES.settings.payroll),
        });
    }
    if (hasPermission('settings.roles')) {
        settingsChildren.push({
            key: ROUTES.settings.roles,
            icon: <SafetyCertificateOutlined />,
            label: 'Vai trò & Quyền',
            onClick: () => onNavigate(ROUTES.settings.roles),
        });
    }

    // — Phần cứng & Giao diện —
    settingsChildren.push({ type: 'divider' as const });
    if (hasPermission('settings.hardware')) {
        settingsChildren.push({
            key: ROUTES.settings.hardware,
            icon: <PrinterOutlined />,
            label: labelWithTier('Phần cứng', 'P'),
            onClick: () => onNavigate(ROUTES.settings.hardware),
        });
    }
    if (hasPermission('settings.business')) {
        settingsChildren.push({
            key: ROUTES.settings.receiptTemplates,
            icon: <PrinterOutlined />,
            label: 'Mẫu hoá đơn / tem',
            onClick: () => onNavigate(ROUTES.settings.receiptTemplates),
        });
    }
    if (hasPermission('settings.theme')) {
        settingsChildren.push({
            key: ROUTES.settings.appearance,
            icon: <BgColorsOutlined />,
            label: 'Giao diện',
            onClick: () => onNavigate(ROUTES.settings.appearance),
        });
    }
    if (hasPermission('customers.loyalty_config')) {
        settingsChildren.push({
            key: ROUTES.customers.loyaltySettings,
            icon: <GiftOutlined />,
            label: labelWithTier('Loyalty', 'S'),
            onClick: () => onNavigate(ROUTES.customers.loyaltySettings),
        });
    }
    if (hasPermission('settings.notifications')) {
        settingsChildren.push({
            key: ROUTES.settings.notifications,
            icon: <NotificationOutlined />,
            label: 'Thông báo',
            onClick: () => onNavigate(ROUTES.settings.notifications),
        });
    }
    if (isOwner) {
        settingsChildren.push({
            key: ROUTES.settings.general,
            icon: <CarOutlined />,
            label: 'Cấu hình chung',
            onClick: () => onNavigate(ROUTES.settings.general),
        });
    }
    if (hasPermission('settings.locale')) {
        settingsChildren.push({
            key: ROUTES.settings.locale,
            icon: <GlobalOutlined />,
            label: 'Ngôn ngữ',
            onClick: () => onNavigate(ROUTES.settings.locale),
        });
    }

    // — Thanh toán & HĐĐT —
    settingsChildren.push({ type: 'divider' as const });
    if (hasPermission('settings.billing')) {
        settingsChildren.push({
            key: ROUTES.settings.billing,
            icon: <CrownOutlined />,
            label: 'Gói dịch vụ',
            onClick: () => onNavigate(ROUTES.settings.billing),
        });
    }
    if (hasPermission('settings.business')) {
        settingsChildren.push({
            key: `${ROUTES.finance.paymentMethods}__shortcut`,
            icon: <BarcodeOutlined />,
            label: 'Phương thức thanh toán',
            onClick: () => onNavigate(ROUTES.finance.paymentMethods),
        });
    }
    if (hasPermission('accounting.einvoice')) {
        settingsChildren.push({
            key: `${ROUTES.finance.einvoiceSettings}__shortcut`,
            icon: <BankOutlined />,
            label: labelWithTier('Cài đặt HĐĐT', 'S'),
            onClick: () => onNavigate(ROUTES.finance.einvoiceSettings),
        });
    }
    if (hasPermission('settings.data_export')) {
        settingsChildren.push({
            key: ROUTES.settings.dataExport,
            icon: <DatabaseOutlined />,
            label: 'Xuất dữ liệu',
            onClick: () => onNavigate(ROUTES.settings.dataExport),
        });
    }
    if (hasPermission('settings.data_export')) {
        settingsChildren.push({
            key: `${ROUTES.imports.index}__shortcut`,
            icon: <DatabaseOutlined />,
            label: labelWithTier('Import dữ liệu', 'S'),
            onClick: () => onNavigate(ROUTES.imports.index),
        });
    }

    // — Bảo mật & API —
    settingsChildren.push({ type: 'divider' as const });
    if (hasPermission('settings.api_keys')) {
        settingsChildren.push({
            key: ROUTES.settings.apiKeys,
            icon: <KeyOutlined />,
            label: labelWithTier('API Keys', 'E'),
            onClick: () => onNavigate(ROUTES.settings.apiKeys),
        });
    }
    if (hasPermission('settings.audit_log')) {
        settingsChildren.push({
            key: ROUTES.settings.auditLog,
            icon: <AuditOutlined />,
            label: labelWithTier('Audit log', 'P'),
            onClick: () => onNavigate(ROUTES.settings.auditLog),
        });
    }

    // — Cá nhân —
    settingsChildren.push({ type: 'divider' as const });
    settingsChildren.push({
        key: ROUTES.settings.profile,
        icon: <UserOutlined />,
        label: 'Hồ sơ cá nhân',
        onClick: () => onNavigate(ROUTES.settings.profile),
    });
    settingsChildren.push({
        key: ROUTES.settings.security,
        icon: <LockOutlined />,
        label: 'Bảo mật',
        onClick: () => onNavigate(ROUTES.settings.security),
    });

    // — Hỗ trợ —
    settingsChildren.push({ type: 'divider' as const });
    settingsChildren.push({
        key: ROUTES.support.home,
        icon: <QuestionCircleOutlined />,
        label: 'Trung tâm hỗ trợ',
        onClick: () => onNavigate(ROUTES.support.home),
    });
    settingsChildren.push({
        key: ROUTES.support.changelog,
        icon: <AppstoreOutlined />,
        label: 'Changelog',
        onClick: () => onNavigate(ROUTES.support.changelog),
    });

    items.push(applyPopupLayout({
        key: '/settings',
        icon: <SettingOutlined />,
        ...widePopup('Cài đặt'),
        children: settingsChildren,
    }, settingsChildren));

    return items;
}
